import styled, { useTheme } from 'styled-components';
import { prop } from 'styled-tools';
import ActivityCard from './ActivityCard';
import { DashboardPayload } from '../../hydra';

const IS_CAREERIST = process.env.REACT_APP_IS_CAREERIST === 'true';

const DashboardActivitiesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 52px;
  margin-bottom: 102px;
`;

const MissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);

  @media screen and (max-width: 1279px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
  column-gap: 32px;
  row-gap: 8px;
`;

const MissionHeader = styled.p`
  font-style: normal;
  font-weight: 500;
  font-size: 27px;
  line-height: 36px;
  text-decoration: none;
  margin-top: 20px !important;
  padding-bottom: 14px !important;
  color: ${prop('theme.colors.textRegular')};
`;

const GridMissionColumn = styled.div`
  display: flex;
  flex-direction: column;

  min-width: 222px;
  max-width: 280px;

  @media only screen and (min-width: 960px) and (max-width: 1365px) {
    min-width: 180px;
    max-width: 236px;
  }
`;

const GridActivitiesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
`;

export default function DashboardGrid({
  missions,
  getActivityUrl,
  isHighlightingOutstanding,
}: {
  missions: Array<DashboardPayload['missions'][0]>;
  getActivityUrl: (
    mission: DashboardPayload['missions'][0],
    activity: DashboardPayload['missions'][0]['activities'][0]
  ) => string;
  isHighlightingOutstanding: boolean;
}) {
  const missionsByPart = Object.values(
    missions.reduce((acc: { [key: number]: typeof missions }, mission) => {
      if (!acc[mission.week.index]) {
        acc[mission.week.index] = [];
      }
      acc[mission.week.index].push(mission);
      return acc;
    }, {})
  );

  // Flatten all activities across all missions
  const allActivities = missions.flatMap((mission) => mission.activities);

  // Helper function to find the previous core activity
  const findPreviousCoreActivity = (currentIndex: number) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (allActivities[i].isCore) {
        return allActivities[i];
      }
    }
    return null;
  };

  const isActivityLocked = (
    _: DashboardPayload['missions'][0]['activities'][0],
    activityIndex: number
  ) => {
    if (!IS_CAREERIST || activityIndex === 0) {
      return false;
    }

    const previousCore = findPreviousCoreActivity(activityIndex);
    return (previousCore && !previousCore.isCompleted) || false;
  };

  return (
    <DashboardActivitiesWrapper>
      {missionsByPart.map((missions, index) => (
        <PartGrid
          key={`part-${index}`}
          missions={missions}
          getActivityUrl={getActivityUrl}
          isHighlightingOutstanding={isHighlightingOutstanding}
          allActivities={allActivities}
          isActivityLocked={isActivityLocked}
        />
      ))}
    </DashboardActivitiesWrapper>
  );
}

function PartGrid({
  missions,
  getActivityUrl,
  isHighlightingOutstanding,
  allActivities,
  isActivityLocked,
}: {
  missions: Array<DashboardPayload['missions'][0]>;
  getActivityUrl: (
    mission: DashboardPayload['missions'][0],
    activity: DashboardPayload['missions'][0]['activities'][0]
  ) => string;
  isHighlightingOutstanding: boolean;
  allActivities: DashboardPayload['missions'][0]['activities'][0][];
  isActivityLocked: (
    activity: DashboardPayload['missions'][0]['activities'][0],
    activityIndex: number
  ) => boolean;
}) {
  const part = missions[0].week;
  const theme = useTheme();

  return (
    <div>
      <h3
        style={{
          marginTop: '52px',
          color: theme.colors.textRegular,
          marginBottom: '4px',
        }}
        className="dim-content"
      >
        Part {part.index + 1}
      </h3>
      <MissionsGrid>
        {missions.map((mission) => (
          <GridMissionColumn key={`mission-column-${mission.slug}`}>
            <MissionHeader className="dim-content">
              {mission.index + 1}
            </MissionHeader>
            <GridActivitiesColumn>
              {mission.activities.map((activity, activityIndex) => {
                const activityIndexInAll = allActivities.findIndex(
                  (a) => a.slug === activity.slug
                );

                return (
                  <ActivityCard
                    key={`activity-card-${activity.slug}`}
                    isHighlightingOutstanding={isHighlightingOutstanding}
                    href={getActivityUrl(mission, activity)}
                    isFirstActivityInMission={activityIndex === 0}
                    isLastActivityInMission={
                      activityIndex === mission.activities.length - 1
                    }
                    activity={activity}
                    isLocked={isActivityLocked(activity, activityIndexInAll)}
                  />
                );
              })}
            </GridActivitiesColumn>
          </GridMissionColumn>
        ))}
      </MissionsGrid>
    </div>
  );
}
